import * as React from 'react';
import { ISession, ISessionData } from 'shared/consts/auth.const';
import { getPermissions } from 'services/api-iam.service';
import { LOCAL_STORAGE_KEY } from 'shared/consts/auth.const';
import {
  getSession,
  getSessionData,
  invalidateAccessTokenInHeader,
  removeLocalStorageItem,
  renewToken,
  setAccessTokenInHeader,
  setupAfterware,
} from 'modules/auth/auth.service';
import { useHistory } from 'react-router-dom';

export type AuthState = {
  permissions: string[];
  roles: string[];
  sessionData: ISessionData | null;
  sessionPayload: ISession | null;
  loginState: LoginStateEnum;
  setSessionPayload: React.Dispatch<React.SetStateAction<ISession | null>>;
  setPermissions: React.Dispatch<React.SetStateAction<string[]>>;
  setSessionData: React.Dispatch<React.SetStateAction<ISessionData | null>>;
  setLoginState: React.Dispatch<React.SetStateAction<LoginStateEnum>>;
  zendeskClient: any;
  isTokenExpired: boolean;
};
export enum LoginStateEnum {
  LOADING = 'Loading',
  LOGGED_IN = 'LoggedIn',
  LOGGED_OUT = 'LoggedOut',
}

export const AuthContext = React.createContext<AuthState>({
  permissions: [],
  roles: [],
  sessionData: null,
  sessionPayload: null,
  loginState: LoginStateEnum.LOADING,
  setSessionPayload: () => null,
  setPermissions: () => null,
  setSessionData: () => null,
  setLoginState: () => null,
  zendeskClient: null,
  isTokenExpired: false,
});
AuthContext.displayName = 'AuthContext';

export const useAuth = () => React.useContext(AuthContext);

export function AuthContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sessionPayload, setSessionPayload] = React.useState(getSession());
  const [permissions, setPermissions] = React.useState<string[]>([]);
  const [sessionData, setSessionData] = React.useState(getSessionData());
  const [loginState, setLoginState] = React.useState<LoginStateEnum>(
    LoginStateEnum.LOADING
  );

  const zendeskClient = React.useMemo(() => {
    if ((window as any)?.ZAFClient) {
      const client = (window as any)?.ZAFClient.init();
      return client;
    }
  }, [(window as any)?.ZAFClient]);

  const history = useHistory();

  function logout() {
    invalidateAccessTokenInHeader();
    setSessionData(null);
    setPermissions([]);
    setSessionPayload(null);
    removeLocalStorageItem(LOCAL_STORAGE_KEY.SESSION);
    setLoginState(LoginStateEnum.LOGGED_OUT);
  }

  React.useEffect(() => {
    async function initSession() {
      setupAfterware(
        (newSession) => {
          setSessionPayload(newSession);
          setLoginState(LoginStateEnum.LOGGED_IN);
          //get permission?
        }
      );

      const currentSession = getSession();
      if (!currentSession) {
        // make sure it will go to login page
        setLoginState(LoginStateEnum.LOGGED_OUT);
        history.replace(`/login`);
        return;
      }

      if (isTokenExpired) {
        /**
         * refresh -> SUCCESS -> permission
         * refresh -> FAILED -> logout
         *  */

        renewToken(
          (newSession) => {
            setSessionPayload(newSession);
            setLoginState(LoginStateEnum.LOGGED_IN);
            //get permission ?
          },
          () => {
            logout();
            history.push('/login');
          }
        );
      } else {
        // access-token still vaild
        setAccessTokenInHeader();
        const getRoles = sessionData
          ? await getPermissions({
              namespace: sessionData?.namespace,
              userId: sessionData?.sub,
            }).catch((_) => [])
          : [];
        if (getRoles.length !== 0) {
          setLoginState(LoginStateEnum.LOGGED_IN);
          setPermissions(getRoles);
        } else {
          logout();
        }
      }
    }
    initSession();
  }, []);

  const isTokenExpired = sessionPayload
    ? sessionPayload.expiresAt < new Date().toISOString()
    : false;

  React.useEffect(() => {
    if (isTokenExpired) {
      setLoginState(LoginStateEnum.LOADING);
      // refresh token here.
      //
    }
  }, [isTokenExpired]);
  return (
    <AuthContext.Provider
      value={{
        permissions,
        roles: [],
        sessionData,
        sessionPayload: sessionPayload,
        loginState: loginState,
        setPermissions,
        setSessionPayload: setSessionPayload,
        setSessionData: setSessionData,
        setLoginState,
        zendeskClient,
        isTokenExpired: isTokenExpired,
      }}
    >
      {loginState === LoginStateEnum.LOADING ? null : children}
    </AuthContext.Provider>
  );
}

// export function AuthContextProvider(child:React.ReactChild){

//     return()
// }
